import { Box, CircularProgress, Grid } from '@mui/material'
import { useContext, useEffect, useState } from 'react'
import { CustomExplain } from '../components/CustomExplain'
import { CustomTitle } from '../components/CustomTitile'
import { Footer } from '../components/Footer'
import { ModalConfirmDialog } from '../components/ModalConfirmDialog'
import { PicturesPaper } from '../components/PicturesPaper'
import { SignageAddButton } from '../components/SignageAddButton'
import { SignageList } from '../components/SignageList'
import { SignageSortButton } from '../components/SignageSortButton'
import { loadStatus } from '../constants/loadStatus'
import { errorMsg } from '../constants/messages'
import { useAuth } from '../hooks/useAuth'
import { useC207DeviceUpdate } from '../hooks/useC207DeviceUpdate'
import { useC701SignageGet } from '../hooks/useC701SignageGet'
import { useC702SignageAdd } from '../hooks/useC702SignageAdd'
import { useC703SignageUpdate } from '../hooks/useC703SignageUpdate'
import { useC704SignageDelete } from '../hooks/useC704SignageDelete'
import { useC901SignedUrlGet } from '../hooks/useC901SignedUrlGet'
import { useCloudStorage } from '../hooks/useCloudStorage'
import type { SignageIf } from '../interfaces/SignageIf'
import { validateFileSize } from '../util/FileSize'
import { LanguageContext } from '../util/LanguageContext'
import { SideMenu } from './SideMenu'

// サイネージ画面
export const Signage = () => {
  // hooks
  const auth = useAuth()
  const c701 = useC701SignageGet()
  const c702 = useC702SignageAdd()
  const c703 = useC703SignageUpdate()
  const c704 = useC704SignageDelete()
  const c901 = useC901SignedUrlGet()
  const c207 = useC207DeviceUpdate()
  const cs = useCloudStorage()
  // 文字列変換用
  const translation = useContext(LanguageContext)
  // ローディングステータス監視
  useEffect(() => {
    if (
      c701.loading === loadStatus.LOADING ||
      c702.loading === loadStatus.LOADING ||
      c703.loading === loadStatus.LOADING ||
      c704.loading === loadStatus.LOADING ||
      c901.loading === loadStatus.LOADING ||
      c207.loading === loadStatus.LOADING ||
      cs.loading === loadStatus.LOADING
    ) {
      auth.setIsDisable(true)
    } else {
      auth.setIsDisable(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    c701.loading,
    c702.loading,
    c703.loading,
    c704.loading,
    c901.loading,
    c207.loading,
    cs.loading,
  ])
  // ダイアログ
  const [openDialog, setOpenDialog] = useState(false)
  const [errorDialog, setErrorDialog] = useState('')
  // サイネージ配列
  const [signages, setSignages] = useState<SignageIf[]>([])
  // 登録日並び替え
  const [orderby, setOrderby] = useState('')

  // サイネージ並べ替えハンドラ
  const SignageSortHandler = async (inputOrderby: string) => {
    const resultUpdate = await c207.execDeviceUpdate(auth.deviceId, inputOrderby)
    if (resultUpdate.success) {
      // サイネージ一覧取得
      const result = await c701.execSignageGet(auth.deviceId)
      if (result.success) {
        if (result.info) {
          setOrderby(result.info.orderby)
          setSignages(result.info.signageList)
        }
      } else {
        setErrorDialog(result.message!)
        setOpenDialog(true)
      }
    } else {
      setErrorDialog(resultUpdate.message!)
      setOpenDialog(true)
    }
  }

  const pattern = /.(jpg|jpeg|png|mp4)$/i
  // 拡張子チェック関数
  const extensionCheck = async (file: string) => {
    const result = pattern.test(file)
    return result
  }

  // サイネージ追加ハンドラ
  const SignageAddHandler = async (text: string, file: File) => {
    // ファイルサイズチェック
    if (!validateFileSize(file)) {
      setErrorDialog(errorMsg.FILE_SIZE_ERROR)
      setOpenDialog(true)
      return
    }

    //拡張子チェック
    const result = await extensionCheck(file.name)

    if (result) {
      // 署名付きURL取得
      const resultUrl = await c901.execSignedUrlGet()
      if (resultUrl.success) {
        const resultCs = await cs.execUploadFile(resultUrl.message!, file)
        if (resultCs.success) {
          //サイネージ登録
          const result = await c702.execSignageAdd(text, file, resultUrl.message!)
          if (result.success) {
            // サイネージ一覧取得
            const result = await c701.execSignageGet(auth.deviceId)
            if (result.success) {
              if (result.info) {
                setOrderby(result.info.orderby)
                setSignages(result.info.signageList)
              }
            } else {
              setErrorDialog(result.message!)
              setOpenDialog(true)
            }
          } else {
            setErrorDialog(result.message!)
            setOpenDialog(true)
          }
        } else {
          setErrorDialog(resultCs.message!)
          setOpenDialog(true)
        }
      } else {
        setErrorDialog(resultUrl.message!)
        setOpenDialog(true)
      }
    } else {
      setErrorDialog(errorMsg.UNSUPPORTED_FILE_ERROR)
      setOpenDialog(true)
    }
  }
  // サイネージ削除ハンドラ
  const SignageDelHandler = async (signage_id: string) => {
    //サイネージ削除
    const result = await c704.execSignageDelete(signage_id)
    if (result.success) {
      // サイネージ一覧取得
      const result = await c701.execSignageGet(auth.deviceId)
      if (result.success) {
        if (result.info) {
          setOrderby(result.info.orderby)
          setSignages(result.info.signageList)
        }
      } else {
        setErrorDialog(result.message!)
        setOpenDialog(true)
      }
    } else {
      setErrorDialog(result.message!)
      setOpenDialog(true)
    }
  }
  // サイネージ編集ハンドラ
  const SignageEditHandler = async (signageId: string, newText: string, file?: File) => {
    let url = ''
    if (file) {
      // ファイルサイズチェック
      if (!validateFileSize(file)) {
        setErrorDialog(errorMsg.FILE_SIZE_ERROR)
        setOpenDialog(true)
        return
      }

      //拡張子チェック
      const result = await extensionCheck(file!.name)
      if (result) {
        // 署名付きURL取得
        const resultUrl = await c901.execSignedUrlGet()
        if (resultUrl.success) {
          const resultCs = await cs.execUploadFile(resultUrl.message!, file!)
          if (resultCs.success) {
            url = resultUrl.message!
          } else {
            setErrorDialog(resultCs.message!)
            setOpenDialog(true)
          }
        } else {
          setErrorDialog(resultUrl.message!)
          setOpenDialog(true)
        }
      } else {
        setErrorDialog(errorMsg.UNSUPPORTED_FILE_ERROR)
        setOpenDialog(true)
        return
      }
    }
    //サイネージ編集
    const result = await c703.execSignageUpdate(signageId, newText, file, url)
    if (result.success) {
      // サイネージ一覧取得
      const result = await c701.execSignageGet(auth.deviceId)
      if (result.success) {
        if (result.info) {
          setOrderby(result.info.orderby)
          setSignages(result.info.signageList)
        }
      } else {
        setErrorDialog(result.message!)
        setOpenDialog(true)
      }
    } else {
      setErrorDialog(result.message!)
      setOpenDialog(true)
    }
  }

  useEffect(() => {
    const fetchData = async () => {
      const result = await c701.execSignageGet(auth.deviceId)
      if (result.success) {
        if (result.info) {
          // console.log(result.info);
          setOrderby(result.info.orderby)
          setSignages(result.info.signageList)
        } else {
        }
      } else {
        setErrorDialog(result.message!)
        setOpenDialog(true)
      }
    }

    fetchData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // ダイアログクローズ
  const handleDialogClose = () => {
    setErrorDialog('')
    setOpenDialog(false)
  }

  return (
    <>
      {openDialog && (
        <ModalConfirmDialog
          open={openDialog}
          text={errorDialog}
          onClick={() => handleDialogClose()}
        />
      )}
      <Box sx={{ display: 'flex', p: 0 }}>
        <Box sx={{ p: 0, width: '338px', flexShrink: 0 }}>
          <SideMenu />
        </Box>
        <Box sx={{ flexGrow: 1, overflowX: 'auto' }} display="flex" flexDirection="column">
          <Box marginTop={13}>
            <CustomTitle>{translation['label_signage']}</CustomTitle>
          </Box>
          <Box>
            <CustomExplain>{translation['message_signage_info']}</CustomExplain>
          </Box>
          <Box
            sx={{
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 9999,
            }}
          >
            {(c701.loading === loadStatus.LOADING ||
              c702.loading === loadStatus.LOADING ||
              c703.loading === loadStatus.LOADING ||
              c704.loading === loadStatus.LOADING ||
              c901.loading === loadStatus.LOADING ||
              c207.loading === loadStatus.LOADING ||
              cs.loading === loadStatus.LOADING) && <CircularProgress />}
          </Box>
          <Box
            sx={{
              marginTop: '40px',
            }}
          >
            <PicturesPaper>
              <Grid container spacing={2} justifyContent="space-between">
                <Grid item lg={6} xl={4}>
                  <SignageAddButton title={translation['label_signage']} onAdd={SignageAddHandler}>
                    {translation['button_add_new']}
                  </SignageAddButton>
                </Grid>{' '}
                <Grid item lg={6} xl={4} justifyContent="flex-end">
                  <SignageSortButton initOrderby={orderby} onSort={SignageSortHandler} />
                </Grid>{' '}
              </Grid>
              <Grid container spacing={2} mt={2}>
                <SignageList
                  items={signages}
                  onEditSignage={(id, newText, file) => SignageEditHandler(id, newText, file)}
                  onDeleteSignage={SignageDelHandler}
                />
              </Grid>
            </PicturesPaper>
          </Box>
        </Box>
      </Box>
      <Footer />
    </>
  )
}
